import type { ExperimentDocument, ExperimentHypothesis, ExperimentInputPayload } from './experimentTypes';

export interface PreRegistrationCheck {
  complete: boolean;
  missing: string[];
}

/**
 * Pre-registration gate (AC-010): an experiment may transition draft→running
 * only when the hypothesis is fully pre-registered. Mirrors the backend rule
 * in ExperimentRegistryService — keep both in sync.
 */
export function checkPreRegistration(
  experiment: Pick<ExperimentDocument, 'type' | 'segmentSplits' | 'runTag' | 'substrate'> & { hypothesis: Partial<ExperimentHypothesis> },
): PreRegistrationCheck {
  const missing: string[] = [];
  const hypothesis = experiment.hypothesis ?? {};

  if (experiment.type === 'analyst' || experiment.type === 'research') {
    // Analyst reviews and research reports are agent-created observations,
    // born complete — no pre-registration requirements. Mirrors the backend
    // rule — keep in sync.
    return { complete: true, missing };
  }

  if (experiment.type === 'emulation') {
    // Emulation runs are learning atoms (D13): hypothesis is optional, but the
    // run must be identifiable — runTag + substrate identity are the warehouse
    // JOIN keys. Mirrors the backend rule — keep both in sync.
    if (!experiment.runTag?.trim()) missing.push('runTag');
    if (!experiment.substrate?.funnelId) missing.push('substrate funnelId');
    if (!experiment.substrate?.versionId) missing.push('substrate versionId');
    if (!experiment.substrate?.alias) missing.push('substrate alias');
    return { complete: missing.length === 0, missing };
  }

  if (!hypothesis.statement?.trim()) missing.push('hypothesis statement');
  if (!hypothesis.primaryMetric?.trim()) missing.push('primary metric');
  if (!hypothesis.successCriteria?.trim()) missing.push('success criteria');
  if (!hypothesis.guardrails?.length) missing.push('at least one guardrail metric');
  if (experiment.type === 'discovery' && !experiment.segmentSplits?.length) {
    missing.push('pre-registered segment splits');
  }
  if (experiment.type === 'web_ab') {
    const withRef = experiment as { abTestRef?: { abTestId?: string } };
    if (!withRef.abTestRef?.abTestId) missing.push('linked A/B test');
  }
  if (experiment.type === 'pricing') {
    // Pricing gate (plan Increment 3, AC-009): offer identity, ONE declared
    // axis, plan_mix secondary metric, delayed guardrails. Mirrors the backend
    // rule in ExperimentRegistryService — keep both in sync.
    const withPricing = experiment as {
      pricing?: {
        offers?: Array<{ productId?: string; paywallScreenIds?: string[] }>;
        treatmentAxis?: string;
        secondaryMetrics?: string[];
        delayedGuardrails?: string[];
      };
    };
    const pricing = withPricing.pricing;
    if (!pricing) {
      missing.push('pricing spec (offers, treatment axis, plan-mix metric, delayed guardrails)');
    } else {
      if (!pricing.offers?.length) {
        missing.push('offer identity (at least one offer with product/paywall ids)');
      } else {
        pricing.offers.forEach((offer, index) => {
          if (!offer.productId?.trim()) missing.push(`offer[${index}] productId`);
          // Paywall screen ids are a LAUNCH-time concern (§6 7a-b, AC-081):
          // a draft registers without screens — checkLaunchReadiness owns them.
        });
      }
      if (!pricing.treatmentAxis?.trim()) missing.push('declared treatment axis');
      if (!pricing.secondaryMetrics?.includes('plan_mix')) {
        missing.push('plan_mix as a secondary metric');
      }
      if (!pricing.delayedGuardrails?.length) missing.push('at least one delayed guardrail');
    }
  }

  return { complete: missing.length === 0, missing };
}

/**
 * Launch-readiness gate (§6 7a-b, AC-081): pre-registration PLUS launch-time
 * bindings — pricing offers must name their paywall screens before draft→running.
 * Mirrors the backend `checkLaunchReadiness` in ExperimentRegistryService —
 * keep both in sync.
 */
export function checkLaunchReadiness(
  experiment: Parameters<typeof checkPreRegistration>[0],
): PreRegistrationCheck {
  const base = checkPreRegistration(experiment);
  const missing = [...base.missing];
  if (experiment.type === 'pricing') {
    const withPricing = experiment as {
      pricing?: { offers?: Array<{ paywallScreenIds?: string[] }> };
    };
    withPricing.pricing?.offers?.forEach((offer, index) => {
      if (!offer.paywallScreenIds?.length) missing.push(`offer[${index}] paywall screen ids`);
    });
  }
  return { complete: missing.length === 0, missing };
}

export function experimentInputIsValid(input: ExperimentInputPayload): { valid: boolean; error?: string } {
  if (!input.name?.trim()) return { valid: false, error: 'Experiment name is required' };
  if (!input.type) return { valid: false, error: 'Experiment type is required' };
  return { valid: true };
}
